import React, { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  Box,
  Typography,
  styled,
} from '@mui/material'
import SwapHoriz from '@mui/icons-material/SwapHoriz'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { useNavigate } from 'react-router'
import { getUserRoles } from 'src/mentor/layout/utils'
import { getAvailablePortals } from 'src/shared/config/portalConfig'

const PortalSwitchButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'darkText',
})(({ theme, darkText }) => ({
  width: '37.21px',
  height: '37.21px',
  borderRadius: '7px',
  cursor: 'pointer',
  background: darkText ? 'rgba(0, 0, 0, 0.05)' : theme?.themeC4,
  color: '#ffffff',
  padding: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&:hover': {
    background: darkText ? 'rgba(0, 0, 0, 0.1)' : theme?.themeC4,
    opacity: darkText ? 1 : 0.9,
  },
  transition: 'all 0.2s ease',
  '@media (max-width: 720px)': {
    width: '27.21px',
    height: '27.21px',
  },
}))

const PortalMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(67, 56, 202, 0.15)',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(67, 56, 202, 0.25), 0 4px 12px rgba(0, 0, 0, 0.1)',
    minWidth: '280px',
    maxWidth: '320px',
    marginTop: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    overflow: 'hidden',
  },
  '& .MuiMenuItem-root': {
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    padding: theme.spacing(1.25, 2),
    borderRadius: '8px',
    margin: theme.spacing(0.25, 1),
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(67, 56, 202, 0.06)',
      transform: 'translateX(2px)',
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '&.active': {
      background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)',
      borderLeft: '3px solid rgba(67, 56, 202, 0.8)',
      fontWeight: 600,
      color: '#4338ca',
      '&:hover': {
        backgroundColor: 'rgba(67, 56, 202, 0.15)',
        transform: 'translateX(0)',
      },
      '& .MuiSvgIcon-root': {
        color: '#4338ca',
      },
    },
  },
}))

// Portal configuration is now imported from centralized config

export default function PortalSwitcher({ darkText = false, isMobile = false }) {
  const navigate = useNavigate()
  const userRoles = getUserRoles()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const availablePortals = getAvailablePortals(userRoles)

  // Show portal switcher if user has access to more than one portal
  // This allows users with ROLE_USER or ROLE_PATIENT to see the switcher if they have multiple portals
  if (availablePortals.length <= 1) {
    return null
  }

  const handlePortalMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePortalMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePortalSwitch = (portal) => {
    handlePortalMenuClose()

    setTimeout(() => {
      if (portal.external) {
        window.open(portal.path, '_blank', 'noopener,noreferrer')
      } else if (portal.path === '/') {
        sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
        sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
        sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        localStorage.removeItem('lastChosenFlow')
        window.location.replace('/')
      } else {
        // Update lastChosenFlow for portals that use it (for dual-role user redirect preference)
        if (portal.id === 'clinic-assistant') {
          localStorage.setItem('lastChosenFlow', 'clinic-assistant')
        } else if (portal.id === 'kiosk') {
          localStorage.setItem('lastChosenFlow', 'kiosk')
        } else if (portal.id === 'mentor') {
          localStorage.setItem('lastChosenFlow', 'mentor')
        } else if (portal.id === 'clinic') {
          localStorage.setItem('lastChosenFlow', 'clinic')
        } else if (portal.id === 'support') {
          localStorage.setItem('lastChosenFlow', 'support')
        } else if (portal.id === 'business') {
          localStorage.setItem('lastChosenFlow', 'business')
        }
        navigate(portal.path)
      }
    }, 100)
  }

  const isCurrentPortal = (portal) => {
    if (portal.external) return false

    const currentPath = window.location.pathname

    if (portal.path === '/') {
      return (
        currentPath === '/' ||
        (!currentPath.startsWith('/support-home') &&
          !currentPath.startsWith('/mentor-home') &&
          !currentPath.startsWith('/clinic-home') &&
          !currentPath.startsWith('/ca-home') &&
          !currentPath.startsWith('/kiosk') &&
          !currentPath.startsWith('/workplace'))
      )
    }

    return currentPath === portal.path || currentPath.startsWith(portal.path + '/')
  }

  return (
    <>
      <PortalSwitchButton
        onClick={handlePortalMenuClick}
        aria-label="Switch portal"
        size="small"
        darkText={darkText}
      >
        <SwapHoriz sx={{ fontSize: isMobile ? 18 : 20 }} />
      </PortalSwitchButton>

      <PortalMenu
        anchorEl={anchorEl}
        open={open}
        onClose={handlePortalMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isMobile ? 'center' : 'right',
        }}
        PaperProps={{
          sx: {
            maxHeight: isMobile ? '60vh' : '70vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.3)',
              },
            },
          },
        }}
      >
        {availablePortals.map((portal) => {
          const Icon = portal.icon
          const isActive = isCurrentPortal(portal)
          const shouldDisable = isActive && !portal.external && portal.path !== '/'
          return (
            <MenuItem
              key={portal.id}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handlePortalSwitch(portal)
              }}
              className={isActive ? 'active' : ''}
              disabled={shouldDisable}
              sx={{
                '&.active': {
                  '& .portal-icon': {
                    color: '#4338ca',
                  },
                  '& .portal-name': {
                    color: '#4338ca',
                    fontWeight: 600,
                  },
                  '& .portal-badge': {
                    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                    color: '#4338ca',
                    border: '1px solid rgba(67, 56, 202, 0.3)',
                  },
                },
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} width="100%">
                <Icon
                  className="portal-icon"
                  sx={{
                    fontSize: 20,
                    color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.6)',
                    transition: 'color 0.2s ease',
                  }}
                />
                <Box flex={1}>
                  <Typography
                    className="portal-name"
                    variant="body2"
                    fontWeight={isActive ? 600 : 500}
                    sx={{
                      color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.87)',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {portal.name}
                  </Typography>
                </Box>
                {isActive && (
                  <Box
                    className="portal-badge"
                    sx={{
                      height: '20px',
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '0 6px',
                      display: 'flex',
                      alignItems: 'center',
                      background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                      color: '#4338ca',
                      border: '1px solid rgba(67, 56, 202, 0.3)',
                      borderRadius: '10px',
                    }}
                  >
                    Current
                  </Box>
                )}
                {portal.external && (
                  <OpenInNew
                    sx={{
                      fontSize: 16,
                      color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.5)',
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          )
        })}
      </PortalMenu>
    </>
  )
}
